import { PLAN_TIERS, PLANS, type PlanTier } from '@/shared/config/plans';
import type { MasterProfile } from '@/shared/types';

import type { JsonLd } from './headTags';
import { absoluteUrl, BRAND, SITE_URL } from './site';

/**
 * schema.org builders. Each returns a standalone JSON-LD object describing
 * only what the page really shows — structured data that promises more than
 * the page delivers is a search-quality violation.
 */
const CONTEXT = 'https://schema.org';

export const organization = (description: string): JsonLd => ({
  '@context': CONTEXT,
  '@type': 'Organization',
  name: BRAND,
  url: `${SITE_URL}/`,
  description,
});

export const webSite = (homeUrl: string, locale: string): JsonLd => ({
  '@context': CONTEXT,
  '@type': 'WebSite',
  name: BRAND,
  url: homeUrl,
  inLanguage: locale,
});

/** The product and its plans, priced from the same matrix billing uses. */
export const softwareApplication = (
  description: string,
  planName: (tier: PlanTier) => string,
): JsonLd => ({
  '@context': CONTEXT,
  '@type': 'SoftwareApplication',
  name: BRAND,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description,
  offers: PLAN_TIERS.map((tier) => ({
    '@type': 'Offer',
    name: planName(tier),
    price: PLANS[tier].price.UAH,
    priceCurrency: 'UAH',
  })),
});

export const faqPage = (items: { question: string; answer: string }[]): JsonLd => ({
  '@context': CONTEXT,
  '@type': 'FAQPage',
  mainEntity: items.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

export const breadcrumbList = (items: { name: string; path: string }[]): JsonLd => ({
  '@context': CONTEXT,
  '@type': 'BreadcrumbList',
  itemListElement: items.map(({ name, path }, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name,
    item: absoluteUrl(path),
  })),
});

/** A master's showcase as a local beauty business with its price list and reviews. */
export const beautySalon = (master: MasterProfile, url: string, city: string): JsonLd => ({
  '@context': CONTEXT,
  '@type': 'BeautySalon',
  name: master.name,
  description: master.speciality,
  url,
  image: master.avatarUrl,
  address: { '@type': 'PostalAddress', addressLocality: city, addressCountry: 'UA' },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: master.rating,
    reviewCount: master.reviewsCount,
  },
  review: master.reviews.slice(0, 5).map((review) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: review.author },
    datePublished: review.date,
    reviewBody: review.text,
    reviewRating: { '@type': 'Rating', ratingValue: review.rating, bestRating: 5 },
  })),
  makesOffer: master.services.map((service) => ({
    '@type': 'Offer',
    price: service.price,
    priceCurrency: 'UAH',
    itemOffered: { '@type': 'Service', name: service.name },
  })),
});
